/**
 * Minimal episode data for display components.
 * Matches the CloudEpisode shape from @bastion/data, but components
 * only depend on this subset so they remain decoupled.
 */
export interface EpisodeItem {
  id: string;
  created_at: string;
  source: 'teleop' | 'inference';
  task_description?: string | null;
  task_rating?: number | null;
  hostname?: string | null;
  frame_count?: number | null;
  fps?: number | null;
  reviewer_id?: string | null;
  reviewed_at?: string | null;
  segment_count?: number;
  has_full_subtask_coverage?: boolean | null;
  collector_id?: string | null;
  hardware_version?: string | null;
  sop_id?: string | null;
  tags?: string[] | null;
  s3_uri?: string;
  has_audio?: boolean | null;
  has_transcript?: boolean | null;
}

/** Subtask focus target for navigation from subtask list to episode detail */
export interface FocusSubtask {
  segmentId: string;
  episodeId: string;
  startFrame: number;
  endFrame: number;
  label: string;
}

/** Rating values used in the system, ordered from worst to best. */
export const TASK_RATING_VALUES = [0, 1, 3, 5] as const;

export type TaskRating = (typeof TASK_RATING_VALUES)[number];

export const TASK_RATING_LABELS: Record<TaskRating, string> = {
  0: 'Fail',
  1: 'Poor',
  3: 'Partial',
  5: 'Perfect',
};

export const TASK_RATING_FILTER_OPTIONS = [...TASK_RATING_VALUES]
  .reverse()
  .map((value) => ({
    value: String(value),
    label: `${TASK_RATING_LABELS[value]} (${value})`,
  }));

export function isTaskRating(value: unknown): value is TaskRating {
  return (
    typeof value === 'number' &&
    (TASK_RATING_VALUES as readonly number[]).includes(value)
  );
}

/** View mode for the episode browser */
export type ViewMode = 'grid' | 'table';

/** Sort options for the episode list */
export interface SortConfig {
  field: 'created_at' | 'task_rating' | 'frame_count';
  order: 'asc' | 'desc';
}

/** Subtask coverage filter values */
export type SubtaskCoverage = 'full' | 'partial' | 'none';

/**
 * Per-card / per-row field visibility. The same set governs both the grid
 * (meta strip on the card) and the table (per-column hide). Adding a new
 * field here means it has a checkbox in the Display Options popover and
 * shows up in the URL as `?fields_hidden=...`.
 */
export const EPISODE_CARD_FIELD_KEYS = [
  'source',
  'rating',
  'sop',
  'collector',
  'hostname',
  'duration',
  'audio',
  'age',
  'reviewed',
] as const;

export type EpisodeCardFieldKey = (typeof EPISODE_CARD_FIELD_KEYS)[number];

export const EPISODE_CARD_FIELD_LABELS: Record<EpisodeCardFieldKey, string> = {
  source: 'Source (teleop/inference)',
  rating: 'Rating',
  sop: 'SOP',
  collector: 'Collector',
  hostname: 'Robot',
  duration: 'Duration',
  audio: 'Audio',
  age: 'Age (e.g. "2 hours ago")',
  reviewed: 'Reviewed',
};

export const EPISODE_SIDEBAR_SECTION_KEYS = [
  'activity', 'ratings', 'duration', 'robots', 'collectors', 'sources',
] as const;

export const DEFAULT_EPISODE_SIDEBAR_SECTIONS = ['activity', 'ratings', 'duration'];

/** Sort, density, and layout preferences (not URL-encoded filter chips). */
export interface EpisodeDisplayPreferences {
  viewMode: ViewMode;
  sort: SortConfig;
  pageSize: number;
  /**
   * Card / row fields hidden by the user. Keys are from
   * `EPISODE_CARD_FIELD_KEYS`. Empty/missing = everything visible.
   * Optional so existing call sites that build a `EpisodeDisplayPreferences`
   * inline don't have to know about field-visibility.
   */
  hiddenFields?: string[];
  /** Visible summary sections. An empty array hides all sections. */
  sidebarSections?: string[];
}

export const DEFAULT_EPISODE_DISPLAY: EpisodeDisplayPreferences = {
  viewMode: 'table',
  sort: { field: 'created_at', order: 'desc' },
  pageSize: 100,
  hiddenFields: [],
  sidebarSections: DEFAULT_EPISODE_SIDEBAR_SECTIONS,
};

export type { FilterState } from '@bastion/list-view';
export { EMPTY_FILTER_STATE } from '@bastion/list-view';
